import React, { useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import Image from '../AppImage';

const PostDetailsOverlay = ({ isOpen, onClose, post }) => {
  const overlayRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      contentRef?.current?.focus();
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen || !post) return null;

  return (
    <div
      className="post-details-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="post-details-title"
      ref={overlayRef}
    >
      <div className="post-details-backdrop" />
      <div
        className="post-details-content"
        ref={contentRef}
        tabIndex={-1}
      >
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
          <h3 id="post-details-title" className="text-h4 font-heading">
            Post Details
          </h3>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-base touch-target"
            aria-label="Close post details"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
              <Icon name="User" size={24} color="var(--color-muted-foreground)" />
            </div>
            <div>
              <p className="font-medium text-foreground">{post?.author || 'Anonymous User'}</p>
              <p className="caption text-muted-foreground">{post?.timestamp || '2 hours ago'}</p>
            </div>
          </div>

          {post?.image && (
            <div className="mb-4 rounded-xl overflow-hidden">
              <Image
                src={post?.image}
                alt={post?.caption || 'Post image'}
                className="w-full h-auto"
              />
            </div>
          )}

          <div className="mb-6">
            <p className="text-foreground leading-relaxed">
              {post?.caption || 'No caption provided'}
            </p>
            {post?.hashtags && post?.hashtags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {post?.hashtags?.map((tag, index) => (
                  <span
                    key={index}
                    className="caption text-primary font-mono"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-6 py-4 border-y border-border">
            <button className="flex items-center gap-2 text-muted-foreground hover:text-error transition-base">
              <Icon name="Heart" size={24} />
              <span className="caption font-medium">{post?.likes || 0}</span>
            </button>
            <button className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-base">
              <Icon name="MessageCircle" size={24} />
              <span className="caption font-medium">{post?.comments || 0}</span>
            </button>
            <button className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-base">
              <Icon name="Share2" size={24} />
              <span className="caption font-medium">Share</span>
            </button>
            <button className="ml-auto text-muted-foreground hover:text-foreground transition-base">
              <Icon name="Bookmark" size={24} />
            </button>
          </div>

          <div className="mt-6">
            <h4 className="text-h5 font-heading mb-4">Comments</h4>
            <div className="space-y-4">
              {post?.commentsList && post?.commentsList?.length > 0 ? (
                post?.commentsList?.map((comment, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="User" size={20} color="var(--color-muted-foreground)" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium caption">{comment?.author}</span>
                        <span className="caption text-muted-foreground">{comment?.time}</span>
                      </div>
                      <p className="text-foreground caption">{comment?.text}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground caption text-center py-8">
                  No comments yet. Be the first to comment!
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDetailsOverlay;